import React, { useState, useEffect } from 'react';
import PerformanceBadge from '../PerformanceBadge';
import NoteTypeTag from '../NoteTypeTag';
import NotePopup from './NotePopup';
import MilestoneCelebration from '../MilestoneCelebration';
import SkillTree from './SkillTree/SkillTree';
import AchievementSequenceModal from './AchievementSequenceModal';
import TodayLessons from '../TodayLessons';
import BrowseCoachingTab from './Bookings/BrowseCoachingTab';
import NotesTab from './NotesTab';
import AttachmentGallery from './AttachmentGallery';
import LoadingSkeleton from './LoadingSkeleton';
import PadelNewsTab from './News/PadelNewsTab';
import PlayerTutorialsTab from './Tutorials/PlayerTutorialsTab';
import PlayerUpdatesTab from './PlayerUpdatesTab';
import { managers, auth as authApi } from '../api';
import SpanishTab from './Spanish/SpanishTab';
import { parseUrlsWithLineBreaks } from '../utils/urlParser';
import '../styles/noteUrls.css';
import { takeNavTarget, onNavTarget } from '../notificationNavigation';
import { getCachedSpanishPref, setCachedSpanishPref } from '../utils/spanishPref';
import usePullToRefresh from './usePullToRefresh';

// Custom hook to detect if tab text should be shown (responsive)
const useShowTabText = () => {
  const [showText, setShowText] = useState(window.innerWidth > 480);

  useEffect(() => {
    const handleResize = () => {
      setShowText(window.innerWidth > 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return showText;
};

function PlayerView({
  user,
  myNotes,
  loading,
  showNotePopup,
  setShowNotePopup,
  popupNote,
  setPopupNote,
  lastSeenNoteId,
  setLastSeenNoteId,
  readNotes,
  markAsRead,
  starredNotes,
  toggleStar,
  showMilestoneCelebration,
  setShowMilestoneCelebration,
  milestoneData,
  showSuccessToast,
  loadingNotes = false,
  handleLogout,
  onSettingsClick
}) {
  // 'skills', 'starred', 'notes', 'personal-notes', 'updates', 'browse-coaching', 'tutorials', 'manager', or 'settings'.
  // After a pull-to-refresh reload the tab you were on is restored (one-shot) instead of jumping back to Skills.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const resume = sessionStorage.getItem('cpResumeTab');
      if (resume) {
        sessionStorage.removeItem('cpResumeTab');
        document.documentElement.classList.add('cp-refreshed'); // fades the page back in (see App.css)
        return resume;
      }
    } catch (e) { /* ignore */ }
    return 'skills';
  });
  const activeTabRef = React.useRef(activeTab);
  activeTabRef.current = activeTab;
  const [showAchievementSequence, setShowAchievementSequence] = useState(false);
  const [newAchievementIds, setNewAchievementIds] = useState([]);
  const [notesSource, setNotesSource] = useState('coach'); // 'coach' or 'personal'

  // Padel Espanol is opt-in (Settings): the tab only exists when it is on
  const [spanishOn, setSpanishOn] = useState(() => getCachedSpanishPref(user && user._id) === true);
  useEffect(() => {
    authApi.getPreferences().then(res => {
      const on = res.data.spanishEnabled === true;
      setSpanishOn(on);
      setCachedSpanishPref(user && user._id, on);
    }).catch(() => {}); // offline: keep what this device remembers
    const onChange = (e) => {
      const enabled = !!(e.detail && e.detail.enabled);
      setSpanishOn(enabled);
      setCachedSpanishPref(user && user._id, enabled);
      if (!enabled) setActiveTab(t => (t === 'spanish' ? 'skills' : t));
    };
    window.addEventListener('spanishPrefChanged', onChange);
    return () => window.removeEventListener('spanishPrefChanged', onChange);
  }, []);

  // Notification tap: switch to the right tab (and section) - see notificationNavigation.js
  const [navTarget, setNavTarget] = useState(null);
  useEffect(() => {
    const apply = () => {
      const target = takeNavTarget('player');
      if (!target) return;
      if (target.tab) setActiveTab(target.tab);
      // Personal Notes is no longer offered: always land on the coach notes
      if (target.notesSource) setNotesSource('coach');
      setNavTarget(target);

      // Specific note: wait for the notes list to render, then scroll to and flash it
      if (target.noteId) {
        let tries = 0;
        const timer = setInterval(() => {
          tries += 1;
          const el = document.querySelector(`[data-note-id="${target.noteId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('note-focus-flash');
            setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
            clearInterval(timer);
          } else if (tries >= 20) {
            clearInterval(timer);
          }
        }, 300);
      }
    };
    apply();
    return onNavTarget(apply);
  }, []);

  // Check if tab text should be shown (responsive)
  const showTabText = useShowTabText();

  // Tabs scroll reference for manual scroll-into-view
  const tabsScrollRef = React.useRef(null);

  // Content scroll reference for scroll-to-top on tab change
  const contentScrollRef = React.useRef(null);
  const [contentKey, setContentKey] = useState(0);
  const [contentFading, setContentFading] = useState(false);
  const reloadApp = React.useCallback(async () => {
    // Refresh in place - no page reload, so no white flash or lost tab. The content fades out, remounts (every tab
    // fetches its data again), the notes reload, then it fades back in.
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    setContentFading(true);
    await wait(240);
    setContentKey((k) => k + 1);
    window.dispatchEvent(new CustomEvent('cpRefreshData'));
    await wait(450);
    setContentFading(false);
  }, []);
  const { pull, progress, ready, refreshing } = usePullToRefresh(contentScrollRef, reloadApp);

  // Manager-specific state
  const [coach, setCoach] = useState(null);
  const [coachStudents, setCoachStudents] = useState([]);
  const [managerLoading, setManagerLoading] = useState(false);
  const [showManagerDetails, setShowManagerDetails] = useState(false);

  // Scroll to top when tab changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Check for new achievements on login
  useEffect(() => {
    if (user && user._id) {
      checkForNewAchievements();
    }
  }, [user?._id]);

  // Load manager data if user is a manager
  useEffect(() => {
    if (user && user.role === 'manager') {
      loadManagerData();
    }
  }, [user?.role]);

  const loadManagerData = async () => {
    try {
      setManagerLoading(true);
      const coachResponse = await managers.getMyCoach();
      setCoach(coachResponse.data);

      const studentsResponse = await managers.getCoachStudents();
      setCoachStudents(studentsResponse.data.students || []);
    } catch (error) {
      console.error('Error loading manager data:', error);
    } finally {
      setManagerLoading(false);
    }
  };

  const checkForNewAchievements = async () => {
    try {
      const playerIdString = String(user._id);
      const storedLastViewTime = localStorage.getItem(`lastAchievementViewTime_${playerIdString}`);
      const lastViewTime = storedLastViewTime ? new Date(storedLastViewTime) : new Date(0);

      // Get achievements data - this would come from your API
      // For now, we'll check if there are unviewed achievements in local storage
      const unviewedIds = localStorage.getItem(`unviewedAchievementIds_${playerIdString}`);

      if (unviewedIds) {
        let ids = [];
        try {
          ids = JSON.parse(unviewedIds);
        } catch (parseError) {
          console.error(`🏆 [Achievement Check] Failed to parse unviewedIds:`, parseError);
          ids = [];
        }

        if (Array.isArray(ids) && ids.length > 0) {
          setNewAchievementIds(ids);
          setShowAchievementSequence(true);
        } else {
        }
      } else {
      }
    } catch (error) {
      console.error('Error checking for new achievements:', error);
    }
  };

  const handleAchievementsComplete = async () => {
    try {
      const playerIdString = String(user._id);

      // Update last view time
      localStorage.setItem(`lastAchievementViewTime_${playerIdString}`, new Date().toISOString());

      // Clear unviewed achievements
      localStorage.removeItem(`unviewedAchievementIds_${playerIdString}`);

      setNewAchievementIds([]);
      setShowAchievementSequence(false);
    } catch (error) {
      console.error('Error marking achievements as viewed:', error);
    }
  };

  // Filter notes based on active tab
  const filteredNotes = activeTab === 'starred'
    ? myNotes.filter(note => starredNotes && starredNotes.includes(note._id))
    : myNotes;

  return (
    <div className="App" style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div className="header" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingBottom: '12px'
      }}>
        {/* ROW 1: Title and Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          width: '100%'
        }}>
          <img src="/icons/icon-192.png" alt="CoachPad" width="44" height="44" style={{ width: 44, height: 44, borderRadius: 12, display: "block", boxShadow: "0 2px 8px rgba(0,0,0,0.35)" }} />
          <div className="user-info" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              onClick={onSettingsClick}
              style={{
                background: 'transparent',
                color: 'white',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
              title="Settings"
            >
              ⚙️
            </button>
          </div>
        </div>

        {/* ROW 2: All Notes / Starred Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}>
          <div style={{
            display: 'flex',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '10px',
            padding: '3px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            gap: '4px',
            paddingRight: '8px',
            paddingLeft: '8px',
            '&::-webkit-scrollbar': {
              height: '4px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px'
            }
          }}
          ref={tabsScrollRef}>
            {/* Skills Tab - LEFTMOST */}
            <button
              onClick={() => setActiveTab('skills')}
              data-active={activeTab === 'skills'}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: activeTab === 'skills'
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: activeTab === 'skills' ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>🌳</span>
              {showTabText && <span>Skills</span>}
            </button>

            {/* Notes Tab */}
            <button
              onClick={() => setActiveTab('notes')}
              data-active={(activeTab === 'notes' || activeTab === 'starred')}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: (activeTab === 'notes' || activeTab === 'starred')
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: (activeTab === 'notes' || activeTab === 'starred') ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>📝</span>
              {showTabText && <span>Notes</span>}
            </button>

            {/* Updates Tab */}
            <button
              onClick={() => setActiveTab('updates')}
              data-active={activeTab === 'updates'}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: activeTab === 'updates'
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: activeTab === 'updates' ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>💡</span>
              {showTabText && <span>Updates</span>}
            </button>

            {/* Browse Coaching Tab */}
            <button
              onClick={() => setActiveTab('browse-coaching')}
              data-active={activeTab === 'browse-coaching'}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: activeTab === 'browse-coaching'
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: activeTab === 'browse-coaching' ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>📚</span>
              {showTabText && <span>Coaching</span>}
            </button>

            {/* Tutorials Tab */}
            <button
              onClick={() => setActiveTab('tutorials')}
              data-active={activeTab === 'tutorials'}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: activeTab === 'tutorials'
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: activeTab === 'tutorials' ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>🎬</span>
              {showTabText && <span>Videos</span>}
            </button>

            {/* Padel News Tab */}
            <button
              onClick={() => setActiveTab('padel-news')}
              data-active={activeTab === 'padel-news'}
              style={{
                flex: '0 0 auto',
                padding: '8px 16px',
                background: activeTab === 'padel-news'
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'transparent',
                color: activeTab === 'padel-news' ? '#2f4ba2' : 'white',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                minHeight: '38px'
              }}
            >
              <span style={{ fontSize: '16px' }}>🎾</span>
              {showTabText && <span>Pro Matches</span>}
            </button>

            {/* Spanish Tab (opt-in) */}
            {spanishOn && (
              <button
                onClick={() => setActiveTab('spanish')}
                data-active={activeTab === 'spanish'}
                style={{
                  flex: '0 0 auto',
                  padding: '8px 16px',
                  background: activeTab === 'spanish' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                  color: activeTab === 'spanish' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>🇪🇸</span>
                {showTabText && <span>Español</span>}
              </button>
            )}

            {/* Manager Tab - Only show if user is a manager */}
            {user && user.role === 'manager' && (
              <button
                onClick={() => setActiveTab('manager')}
                data-active={activeTab === 'manager'}
                style={{
                  flex: '0 0 auto',
                  padding: '8px 16px',
                  background: activeTab === 'manager'
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'transparent',
                  color: activeTab === 'manager' ? '#2f4ba2' : 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  minHeight: '38px'
                }}
              >
                <span style={{ fontSize: '16px' }}>👑</span>
                {showTabText && <span>Manager</span>}
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Pull-down refresh indicator */}
      {/* Always mounted so it can glide away smoothly when released early; it follows the finger with no transition while dragging */}
      <div aria-hidden="true" style={{ position: "fixed", left: 0, right: 0, top: 96, display: "flex", justifyContent: "center", zIndex: 500, pointerEvents: "none", transform: "translateY(" + (pull - 36) + "px)", opacity: refreshing || ready ? 1 : Math.min(0.7, progress * 1.4), transition: progress > 0 && !refreshing ? "none" : "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease-out", willChange: "transform, opacity" }}>
        <div className={refreshing ? "pull-spinner pull-spinner--spin" : "pull-spinner"} style={{ transform: refreshing ? undefined : "rotate(" + progress * 300 + "deg) scale(" + (ready ? 1.12 : 0.8 + Math.min(progress, 1) * 0.2) + ")", transition: progress > 0 && !refreshing ? "transform 0.12s linear" : "transform 0.3s ease-out" }} />
      </div>

      {/* Scrollable Content */}
      <div ref={contentScrollRef} style={{
        flex: 1,
        overflow: 'auto',
        overscrollBehaviorY: 'contain',
        WebkitOverflowScrolling: 'touch',
        background: '#f5f5f5',
        padding: '20px'
      }}>
        <div key={contentKey} style={{ width: '100%', opacity: contentFading ? 0 : 1, transform: contentFading ? 'translateY(6px)' : 'none', transition: 'opacity 0.24s ease, transform 0.24s ease' }}>
          {/* Skills View - Always mounted to preserve state */}
          {user && user._id && user._id !== 'undefined' && typeof user._id === 'string' && user._id.length > 0 ? (
            <div style={{
              display: activeTab === 'skills' ? 'block' : 'none'
            }}>
              {/* Skill Tree */}
              <SkillTree
                key={user._id}
                playerName={user.name}
                playerId={user._id}
                user={user}
                isReadOnly={true}
                defaultGender={user.gender || 'm'}
                defaultHandedness={user.handedness || 'r'}
              />
            </div>
          ) : (
            <div style={{
              display: activeTab === 'skills' ? 'block' : 'none',
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              textAlign: 'center',
              color: '#e74c3c'
            }}>
              ❌ Error: Invalid user ID. Please log out and log back in.
              <br />
              <small style={{ color: '#95a5a6', marginTop: '10px' }}>
                User ID: {user?._id || 'not set'}
              </small>
            </div>
          )}

          {/* Browse Coaching Tab Content */}
          {activeTab === 'browse-coaching' && (
            <BrowseCoachingTab user={user} navTarget={navTarget} />
          )}

          {/* Tutorials Tab Content */}
          {activeTab === 'tutorials' && (
            <PlayerTutorialsTab />
          )}

          {/* Spanish Tab Content */}
          {activeTab === 'spanish' && (
            <SpanishTab onOpenSettings={onSettingsClick} />
          )}

          {/* Padel News Tab Content */}
          {activeTab === 'padel-news' && (
            <PadelNewsTab />
          )}

          {/* Updates Tab Content */}
          {activeTab === 'updates' && (
            <PlayerUpdatesTab
              studentId={user._id}
              studentName={user.name}
              currentCoachId={coach?._id || null}
            />
          )}

          {/* Notes Tab Content - Using Unified NotesTab Component */}
          {(activeTab === 'notes' || activeTab === 'starred') && (
            <NotesTab
              notesSource={notesSource}
              onSourceChange={setNotesSource}
              myNotes={myNotes}
              loading={loading}
              loadingNotes={loadingNotes}
              showNotePopup={showNotePopup}
              setShowNotePopup={setShowNotePopup}
              popupNote={popupNote}
              setPopupNote={setPopupNote}
              readNotes={readNotes}
              markAsRead={markAsRead}
              starredNotes={starredNotes}
              toggleStar={toggleStar}
              showMilestoneCelebration={showMilestoneCelebration}
              setShowMilestoneCelebration={setShowMilestoneCelebration}
              milestoneData={milestoneData}
              user={user}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}

          {/* OLD INLINE NOTES RENDERING - REMOVED, REPLACED BY NOTESTAB COMPONENT ABOVE */}
          {false && (loadingNotes || loading ? (
            <div style={{
              background: activeTab === 'starred'
                ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                : 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
              padding: '80px 40px',
              borderRadius: '24px',
              textAlign: 'center',
              color: 'white',
              boxShadow: activeTab === 'starred'
                ? '0 20px 60px rgba(243, 156, 18, 0.3)'
                : '0 20px 60px rgba(47, 75, 162, 0.3)'
            }}>
              <div style={{ fontSize: '80px', marginBottom: '24px' }}>
                {activeTab === 'starred' ? '⭐' : '📭'}
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>
                {activeTab === 'starred' ? 'No starred notes' : 'No notes yet'}
              </div>
              <div style={{ fontSize: '18px', opacity: 0.9 }}>
                {activeTab === 'starred' 
                  ? 'Star important notes to see them here' 
                  : 'Your coach will send you feedback here'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {filteredNotes
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((note, index, array) => {
                  // Milestone calculation
                  const lessonNotesBefore = array
                    .slice(index)
                    .filter(n => n.noteType === 'lesson').length;
                  
                  const isMilestoneBoundary = 
                    note.noteType === 'lesson' && 
                    lessonNotesBefore > 0 && 
                    lessonNotesBefore % 10 === 0 &&
                    (index === array.length - 1 || array[index + 1]?.noteType !== 'lesson' || 
                     array.slice(index + 1).filter(n => n.noteType === 'lesson').length % 10 !== 0);
                  
                  // Check if note is read and starred
                  const isRead = readNotes && Array.isArray(readNotes) && readNotes.includes(note._id);
                  const isStarred = starredNotes && Array.isArray(starredNotes) && starredNotes.includes(note._id);
                  
                  return (
                    <React.Fragment key={note._id}>
                      <div 
                        style={{
                          // ✅ WARM BEIGE/CREAM FOR READ, WHITE FOR UNREAD
                          background: isRead 
                            ? 'linear-gradient(135deg, #fef5e7 0%, #fdebd0 100%)' 
                            : 'white',
                          padding: '24px',
                          borderRadius: '20px',
                          // ✅ DIFFERENT SHADOW
                          boxShadow: isRead
                            ? '0 2px 12px rgba(253, 187, 45, 0.15)'
                            : '0 4px 20px rgba(47, 75, 162, 0.15)',
                          position: 'relative',
                          overflow: 'hidden',
                          // ✅ DIFFERENT BORDER
                          border: isRead 
                            ? '2px solid #f9e79f'
                            : '2px solid rgba(47, 75, 162, 0.2)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* Gradient accent bar at top */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: note.performanceBadge 
                            ? {
                                excellent: 'linear-gradient(90deg, #FFD700 0%, #FFA500 100%)',
                                good: 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)',
                                average: 'linear-gradient(90deg, #3498db 0%, #5dade2 100%)',
                                needs_work: 'linear-gradient(90deg, #e67e22 0%, #f39c12 100%)',
                                keep_trying: 'linear-gradient(90deg, #9b59b6 0%, #8e44ad 100%)'
                              }[note.performanceBadge]
                            : 'linear-gradient(90deg, #2f4ba2 0%, #14246b 100%)'
                        }} />

                        {/* DIAGONAL BADGE */}
                        {note.performanceBadge && (
                          <PerformanceBadge level={note.performanceBadge} size="diagonal" />
                        )}

                        {/* Date and note type row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px',
                          gap: '8px',
                          paddingRight: '60px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 1,
                            minWidth: 0,
                            flexWrap: 'wrap'
                          }}>
                            {/* ✅ READ INDICATOR */}
                            {isRead && (
                              <div style={{
                                fontSize: '16px',
                                color: '#27ae60'
                              }}>
                                ✓
                              </div>
                            )}
                            
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: isRead ? '#b7950b' : '#7f8c8d'
                            }}>
                              {new Date(note.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                            
                            {/* Note Type Tag */}
                            {note.noteType && (
                              <NoteTypeTag type={note.noteType} size="small" audience="player" />
                            )}
                          </div>
                        </div>
                        
                        {/* Note preview */}
                        <div style={{
                          fontSize: '17px',
                          fontWeight: '500',
                          lineHeight: '1.6',
                          // isRead card background is a light cream/gold GRADIENT
                          // (linear-gradient bypasses the app's dark-theme
                          // override, which only catches solid background-color
                          // values) - it stays genuinely light, so this needs a
                          // dark, rich color, not a light one. Unread card
                          // background is a plain 'white' keyword, which DOES
                          // get auto-darkened by the dark-theme override - so
                          // this needs a normal dark color (readable on the
                          // white card in light theme), not a hardcoded light
                          // one: the override flips it to light automatically
                          // once the card itself gets swept dark.
                          color: isRead ? '#4a3200' : '#333',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          paddingRight: '60px'
                        }}>
                          {parseUrlsWithLineBreaks(note.text)}
                        </div>

                        {/* Photo Gallery - Display photos if they exist */}
                        {(() => {
                          return note.attachments && note.attachments.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                              <AttachmentGallery
                                attachments={note.attachments}
                                isCoach={false}
                                noteId={note._id}
                              />
                            </div>
                          );
                        })()}

                        {/* Action buttons row */}
                        <div style={{
                          marginTop: '16px',
                          display: 'flex',
                          gap: '8px',
                          paddingTop: '12px',
                          borderTop: `1px solid ${isRead ? '#f9e79f' : '#f0f0f0'}`
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPopupNote(note);
                              setShowNotePopup(true);
                            }}
                            style={{
                              flex: 1,
                              padding: '10px 16px',
                              background: isRead
                                ? 'linear-gradient(135deg, #ed8d3e 0%, #be5307 100%)'
                                : 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: '600',
                              boxShadow: isRead
                                ? '0 2px 8px rgba(212, 106, 13, 0.88)'
                                : '0 2px 8px rgba(47, 75, 162, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            👁️ View Full Note
                          </button>
                          
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(note.text);
                                e.target.innerHTML = '✓';
                                e.target.style.background = 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
                                setTimeout(() => {
                                  e.target.innerHTML = '📋';
                                  e.target.style.background = 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
                                }, 2000);
                              } catch (err) {
                                console.error('Failed to copy:', err);
                                alert('Failed to copy text');
                              }
                            }}
                            style={{
                              padding: '10px 16px',
                              background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '16px',
                              fontWeight: '600',
                              boxShadow: '0 2px 8px rgba(149, 165, 166, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '50px',
                              cursor: 'pointer'
                            }}
                          >
                            📋
                          </button>

                          {/* ✅ STAR BUTTON - MOVED TO BOTTOM RIGHT */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleStar(note._id);
                            }}
                            style={{
                              padding: '10px 16px',
                              background: isStarred 
                                ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                                : 'rgba(0, 0, 0, 0.05)',
                              color: isStarred ? 'white' : '#95a5a6',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '18px',
                              fontWeight: '600',
                              boxShadow: isStarred 
                                ? '0 2px 8px rgba(243, 156, 18, 0.3)'
                                : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '50px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {isStarred ? '⭐' : '☆'}
                          </button>
                        </div>
                      </div>

                      {/* Milestone Separator */}
                      {isMilestoneBoundary && activeTab === 'all' && (
                        <div style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          padding: '20px 24px',
                          borderRadius: '16px',
                          textAlign: 'center',
                          boxShadow: '0 8px 24px rgba(255, 215, 0, 0.3)',
                          border: '2px solid #FFD700',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
                            pointerEvents: 'none'
                          }} />
                          <div style={{
                            position: 'relative',
                            zIndex: 1
                          }}>
                            <div style={{
                              fontSize: '48px',
                              marginBottom: '8px'
                            }}>
                              🏆
                            </div>
                            <div style={{
                              fontSize: '22px',
                              fontWeight: '800',
                              color: 'white',
                              textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              marginBottom: '4px'
                            }}>
                              {lessonNotesBefore} Lessons Milestone!
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: 'rgba(255,255,255,0.95)',
                              fontWeight: '600'
                            }}>
                              Amazing progress! Keep it up! 🎉
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          ))}

          {/* Manager Tab Content */}
          {activeTab === 'manager' && user && user.role === 'manager' && (
            <div>
              {/* Coach Card - Clickable to expand */}
              {coach && (
                <div
                  onClick={() => setShowManagerDetails(!showManagerDetails)}
                  style={{
                    backgroundColor: '#e3f2fd',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: showManagerDetails ? '20px' : 0,
                    borderLeft: '4px solid #2196F3',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: showManagerDetails
                      ? '0 4px 12px rgba(33, 150, 243, 0.3)'
                      : '0 2px 4px rgba(33, 150, 243, 0.1)',
                    transform: showManagerDetails ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 10px 0' }}>Connected Coach</h3>
                      <div style={{ fontSize: '18px', fontWeight: 'bold' }}>👨‍🏫 {coach.name}</div>
                      <div style={{ fontSize: '14px', color: '#666' }}>{coach.email}</div>
                    </div>
                    <div style={{ fontSize: '20px', marginLeft: '10px', marginTop: '5px' }}>
                      {showManagerDetails ? '▼' : '▶'}
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded Details */}
              {showManagerDetails && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Today's Lessons Section */}
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px', marginTop: '20px' }}>
                      📅 Today's Lessons
                    </h3>
                    {managerLoading ? (
                      <div style={{ padding: '20px 0' }}>
                        <LoadingSkeleton type="list" count={3} />
                      </div>
                    ) : (
                      <TodayLessons isManagerView={true} coachId={coach._id} />
                    )}
                  </div>

                  {/* Students Section */}
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px' }}>👥 Students & Privileges</h3>
                    {managerLoading ? (
                      <div style={{ padding: '20px 0' }}>
                        <LoadingSkeleton type="card" count={3} />
                      </div>
                    ) : coachStudents.length === 0 ? (
                      <div style={{
                        background: 'white',
                        padding: '40px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: '#999'
                      }}>
                        No students found
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {coachStudents.map(student => (
                          <div
                            key={student._id}
                            style={{
                              padding: '15px',
                              backgroundColor: 'white',
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                          >
                            <div>
                              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                {student.name}
                              </div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                {student.email}
                              </div>
                            </div>
                            <div style={{
                              backgroundColor: student.isManager ? '#fff3e0' : '#e3f2fd',
                              color: student.isManager ? '#e65100' : '#1565c0',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {student.isManager ? '👑 Manager' : '👤 Player'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab Content */}
          {activeTab === 'settings' && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '30px',
                color: '#333'
              }}>
                ⚙️ Settings
              </h2>

              {/* Account Section */}
              <div style={{
                marginBottom: '40px',
                paddingBottom: '30px',
                borderBottom: '1px solid #e0e0e0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  Account Information
                </h3>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      marginBottom: '5px'
                    }}>
                      Name
                    </label>
                    <div style={{
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#333'
                    }}>
                      {user?.name || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      marginBottom: '5px'
                    }}>
                      Email
                    </label>
                    <div style={{
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#333'
                    }}>
                      {user?.email || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      marginBottom: '5px'
                    }}>
                      Role
                    </label>
                    <div style={{
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#333',
                      textTransform: 'capitalize'
                    }}>
                      {user?.role || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      marginBottom: '5px'
                    }}>
                      Gender
                    </label>
                    <div style={{
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{user?.gender === 'f' ? '♀️ Female' : user?.gender === 'm' ? '♂️ Male' : '⚪ Other'}</span>
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      marginBottom: '5px'
                    }}>
                      Handedness
                    </label>
                    <div style={{
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{user?.handedness === 'l' ? '🫲 Left-handed' : '🫱 Right-handed'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <div style={{
                marginBottom: '40px',
                paddingBottom: '30px',
                borderBottom: '1px solid #e0e0e0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  Preferences
                </h3>

                <div style={{
                  padding: '16px',
                  background: '#f0f7ff',
                  borderRadius: '8px',
                  borderLeft: '4px solid #2f4ba2',
                  color: '#333'
                }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>
                    🔔 Notifications
                  </p>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    Notifications are enabled. You'll receive updates about lessons, notes, and coaching sessions.
                  </p>
                </div>
              </div>

              {/* Logout Section */}
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: '#333'
                }}>
                  Session
                </h3>

                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 12px rgba(231, 76, 60, 0.2)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(231, 76, 60, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.2)';
                  }}
                >
                  🚪 Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Note Popup */}
      {showNotePopup && popupNote && (
        <NotePopup
          note={popupNote}
          onClose={() => {
            // Store the note ID before clearing state to avoid accessing stale/null popupNote
            const noteId = popupNote?._id;
            setShowNotePopup(false);
            setPopupNote(null);
            if (noteId) {
              setLastSeenNoteId(noteId);
            }
          }}
          isNewNote={popupNote._id !== lastSeenNoteId}
          markAsRead={markAsRead}
        />
      )}

      {/* Milestone Celebration */}
      {showMilestoneCelebration && milestoneData && (
        <MilestoneCelebration
          milestone={milestoneData.milestone}
          lessonCount={milestoneData.lessonCount}
          onClose={() => setShowMilestoneCelebration(false)}
        />
      )}

      {/* Success Toast for Push Notifications */}
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
          color: 'white',
          padding: '20px 32px',
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(47, 75, 162, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '18px',
          fontWeight: '600',
          animation: 'slideDownToast 0.3s ease-out'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            📝
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700' }}>New Note!</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>From your coach</div>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }

        @keyframes badgeBounce {
          0% {
            opacity: 0;
            transform: scale(0.3) rotate(-10deg);
          }
          50% {
            transform: scale(1.1) rotate(2deg);
          }
          70% {
            transform: scale(0.95) rotate(-1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
      `}</style>

      {/* New Achievements Sequence Modal */}
      <AchievementSequenceModal
        achievementIds={newAchievementIds}
        isOpen={showAchievementSequence}
        onClose={() => setShowAchievementSequence(false)}
        onComplete={handleAchievementsComplete}
      />
    </div>
  );
}

export default PlayerView;